<template>
    <CampaignBanner
        ref="campaignBanner"
        :key="campaignBannerKey"
        :master-template-height="masterTemplate.height"
        :master-template-width="masterTemplate.width"
        :max-height="maxHeight"
        :max-width="maxWidth"
        :scaling-factor="scalingFactor"
        :resize-mode="resizeMode"
        @toggleResizeMode="onToggleResizeMode"
        @updateInternalScalingFactor="internalScalingFactor = $event"
    >
        <template #bannerContainer>
            <template v-if="isSelectable">
                <Checkbox class="campaign-banner__checkbox" :label="masterTemplate._id">
                    <BannerPreview
                        :id="bannerId"
                        :preview-values="previewValues"
                        lazy-load
                        :scaling-factor="internalScalingFactor"
                        :ad-type="masterTemplate.adType"
                        :playback-namespace="playbackNamespace"
                    />
                </Checkbox>
            </template>

            <template v-else>
                <BannerPreview
                    :id="bannerId"
                    :preview-values="previewValues"
                    lazy-load
                    :scaling-factor="internalScalingFactor"
                    :ad-type="masterTemplate.adType"
                    :playback-namespace="playbackNamespace"
                />
            </template>
        </template>

        <template #bannerFooter>
            <BannerFooterName :is-dark-theme="false" :banner-name="bannerName" />
            <BannerFooterControls :banner-id="bannerId" :playback-namespace="playbackNamespace" />
            <MasterTemplateBannerFooterOptions
                :resize-mode="resizeMode"
                :master-template="masterTemplate"
                :options-visible="optionsVisible"
                @updateOptionsVisible="optionsVisible = $event"
                @toggleResizeMode="onToggleResizeMode"
                @remove="$emit('remove')"
            />
        </template>
    </CampaignBanner>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

import type { MasterTemplate } from "@/types";

import BannerPreview from "@/components/Previews/BannerPreview.vue";
import CampaignBanner from "@/components/Campaign/CampaignBanner.vue";
import MasterTemplateBannerFooterOptions from "@/components/Previews/BannerFooter/MasterTemplateBannerFooterOptions.vue";
import BannerFooterName from "@/components/Previews/BannerFooter/BannerFooterName.vue";
import BannerFooterControls from "@/components/Previews/BannerFooter/BannerFooterControls.vue";

import { BannerNamespace } from "@/components/PlaybackControls/usePlaybackController";
import { getBannerIdFromMasterTemplate } from "@/utils/getBannerIframeId";

export default defineComponent({
    name: "MasterTemplatePreview",
    components: {
        CampaignBanner,
        BannerPreview,
        MasterTemplateBannerFooterOptions,
        BannerFooterName,
        BannerFooterControls
    },

    props: {
        masterTemplate: {
            type: Object as PropType<MasterTemplate>,
            required: true
        },
        isSelectable: {
            type: Boolean,
            default: false
        },
        maxWidth: {
            type: Number
        },
        maxHeight: {
            type: Number
        },
        scalingFactor: {
            type: Number,
            default: 0 // auto scaling
        }
    },

    emits: ["remove", "resizeToggle"],

    data() {
        return {
            resizeMode: false,
            playbackNamespace: BannerNamespace.MasterTemplates,
            optionsVisible: false,
            internalScalingFactor: 1
        };
    },

    computed: {
        campaignBannerKey() {
            return `${this.masterTemplate._id}-${this.masterTemplate.modified}`;
        },

        previewValues() {
            return JSON.stringify({
                computedOverwrites: [],
                metadata: [],
                masterTemplate: {
                    _id: this.masterTemplate._id,
                    persistentKey: this.masterTemplate.persistentKey,
                    width: this.masterTemplate.width,
                    height: this.masterTemplate.height,
                    aeInfo: {
                        completed: this.masterTemplate?.aeInfo?.completed,
                        plainlyProjectId: this.masterTemplate?.aeInfo?.plainlyProjectId,
                        plainlyTemplateId: this.masterTemplate?.aeInfo?.plainlyTemplateId
                    },
                    reportingLabel: this.masterTemplate.reportingLabel
                },
                previewUrl: this.masterTemplate.preview,
                psdThumbnail: this.masterTemplate.psdThumbnail,
                modified: this.masterTemplate.modified
            });
        },

        bannerName() {
            return `${this.masterTemplate.width} x ${this.masterTemplate.height} - ${this.masterTemplate.name}`;
        },

        bannerId() {
            return getBannerIdFromMasterTemplate(this.masterTemplate, this.playbackNamespace);
        }
    },

    methods: {
        onToggleResizeMode(resizeMode: boolean) {
            this.resizeMode = resizeMode;
            this.optionsVisible = false;

            this.$emit("resizeToggle", this.resizeMode);
        }
    }
});
</script>
